import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export enum UserRole {
  ADMIN = 'admin',
  GM = 'gm',
  SME = 'sme',
  USER = 'user',
}

export interface UserWithRole {
  oid: string;
  preferred_username: string;
  name: string;
  roles: UserRole[];
  highest_role: UserRole;
}

@Injectable({
  providedIn: 'root'
})
export class RoleService {

  private userRoles$ = new BehaviorSubject<UserRole[]>([]);
  private highestRole$ = new BehaviorSubject<UserRole | null>(null);

  constructor() { }

  /**
   * Set user roles after authentication
   * Called after JWT token is decoded
   */
  setUserRoles(roles: string[], highestRole: string) {
    const parsedRoles = roles.map(r => r as UserRole);
    const parsed = highestRole as UserRole;

    this.userRoles$.next(parsedRoles);
    this.highestRole$.next(parsed);
  }

  /**
   * Get all user roles
   */
  getRoles(): Observable<UserRole[]> {
    return this.userRoles$.asObservable();
  }

  /**
   * Get highest privilege role
   */
  getHighestRole(): Observable<UserRole | null> {
    return this.highestRole$.asObservable();
  }

  /**
   * Check if user has a specific role
   */
  hasRole(role: UserRole): Observable<boolean> {
    return new Observable(observer => {
      this.userRoles$.subscribe(roles => {
        observer.next(roles.includes(role));
      });
    });
  }

  /**
   * Check if user has ANY of the specified roles
   */
  hasAnyRole(roles: UserRole[]): Observable<boolean> {
    return new Observable(observer => {
      this.userRoles$.subscribe(userRoles => {
        observer.next(roles.some(role => userRoles.includes(role)));
      });
    });
  }

  /**
   * Check if user has ALL of the specified roles
   */
  hasAllRoles(roles: UserRole[]): Observable<boolean> {
    return new Observable(observer => {
      this.userRoles$.subscribe(userRoles => {
        observer.next(roles.every(role => userRoles.includes(role)));
      });
    });
  }

  /**
   * Get role display name
   */
  getRoleDisplayName(role: UserRole): string {
    const displayNames: Record<UserRole, string> = {
      [UserRole.ADMIN]: 'Administrator',
      [UserRole.GM]: 'General Manager',
      [UserRole.SME]: 'Subject Matter Expert',
      [UserRole.USER]: 'User',
    };
    return displayNames[role] || role;
  }
}
